/**
 * Anti-corruption layer between the existing POS screens (written against the v1 response shapes) and the v2 API.
 * The screens keep calling their familiar paths; in v2 mode this layer translates each call into the native v2 request(s)
 * and reshapes the answer. Anything it does not know passes straight through to v2 (features, overrides, wallets, waivers...).
 *
 * It is pure: it receives a `raw` function that performs a native v2 request, so it is unit-testable and is also exercised
 * against the REAL v2 server in the server's contract tests.
 */
export type Raw = (method: string, path: string, body?: unknown) => Promise<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
/* eslint-disable @typescript-eslint/no-explicit-any */

const toCamel = (s: string) => s.replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase());

/** snake_case keys -> camelCase, deeply. Values are untouched. */
export function camel<T = any>(v: any): T {
  if (Array.isArray(v)) return v.map(camel) as T;
  if (v && typeof v === "object" && !(v instanceof Date)) return Object.fromEntries(Object.entries(v).map(([k, x]) => [toCamel(k), camel(x)])) as T;
  return v as T;
}

/** The v1 file-export URLs live under /documents/export in v2. */
export function downloadPath(path: string): string {
  return path.replace(/^\/exports\//, "/documents/export/");
}

const tax = (price: number, bp: number | null | undefined) => Math.round(((price ?? 0) * (bp ?? 0)) / 10_000);
const gross = (price: number, bp: number | null | undefined) => price + tax(price, bp);

export function mapSession(s: any) {
  return {
    id: s.id, wristbandTag: s.wristband?.tag ?? s.wristbandTag, zone: s.zone ?? null, startAt: s.enteredAt, endAt: s.paidUntil, purchasedMinutes: s.purchasedMinutes,
    status: s.status, remainingSeconds: s.remainingSeconds, progressPercent: s.progressPercent, overtimeFee: s.overtime?.fee ?? 0, extensionCount: s.extensionCount,
    kid: s.kid, guardian: s.guardian, freeStampAvailable: s.freeStampAvailable,
    stampsNeeded: s.stampsNeeded, billingMode: s.billingMode ?? "prepaid", productId: s.productId ?? null, postpaid: s.postpaid ?? null,
  };
}

export function mapRule(r: any) {
  return {
    id: r.id, code: r.code, name: r.name, nameAr: r.name_ar ?? null, condition: r.condition, kind: r.kind, value: r.value,
    appliesTo: r.params?.applies_to === "total" ? "total" : "pass", params: r.params ?? {},
    requiresApproval: r.requires_approval, requiresNote: r.requires_note, active: r.active, validFrom: r.valid_from ?? null, validTo: r.valid_to ?? null,
  };
}

/** A pass id in the v1 screens is "<productId>:<minutes>" (a ticket from the price rules). */
export function splitTicket(id: string): { productId: string; minutes?: number } {
  const [productId, m] = id.split(":");
  return { productId: productId!, minutes: m ? Number(m) : undefined };
}

const qs = (path: string) => new URL(path, "http://x").searchParams;

export function createGateway(raw: Raw) {
  async function handle(method: string, path: string, body?: any): Promise<{ handled: true; value: any } | { handled: false }> {
    const url = new URL(path, "http://x");
    const p = url.pathname;
    const done = (value: any) => ({ handled: true as const, value });
    let m: RegExpExecArray | null;

    // ───────── sessions & catalog ─────────
    if (method === "GET" && p === "/sessions") {
      const [list, capacity, rules] = await Promise.all([raw("GET", "/sessions"), raw("GET", "/sessions/capacity"), raw("GET", "/sessions/rules")]);
      return done({ sessions: list.sessions.map(mapSession), capacity, rules });
    }
    if (method === "GET" && p === "/catalog") {
      const birth = qs(path).get("birthDate");
      const [cat, tickets] = await Promise.all([raw("GET", "/catalog"), raw("GET", `/sessions/tickets${birth ? `?birthDate=${birth}` : ""}`)]);
      const passes = tickets.tickets.map((t: any) => ({
        id: t.id, sku: "TICKET", barcode: null, name: t.name, nameAr: t.nameAr, kind: "pass", price: t.total, durationMinutes: t.minutes, tracksStock: false, active: true, qtyOnHand: null,
      }));
      const others = cat.products.filter((x: any) => x.kind !== "play_time" && x.kind !== "credit_topup").map((x: any) => {
        const retail = ["addon", "fnb", "merch", "combo"].includes(x.kind);
        return { id: x.id, sku: x.sku, barcode: x.barcode, name: x.name, nameAr: x.name_ar, kind: x.kind, price: retail ? gross(x.price ?? 0, x.tax_bp) : (x.price ?? 0), durationMinutes: null, tracksStock: !!x.consumes_stock, active: true, qtyOnHand: x.qty_on_hand ?? null };
      });
      return done({ products: [...passes, ...others] });
    }
    if (method === "POST" && p === "/sessions/check-in") {
      const t = splitTicket(body.passProductId);
      const send = (payments: any[]) => raw("POST", "/sessions/check-in", {
        guardian: body.guardian, kid: { id: body.kid.id, name: body.kid.name, birthDate: body.kid.birthDate },
        wristband: body.wristbandTag, productId: t.productId, minutes: t.minutes, addons: body.addons, payments, redeemStamps: body.redeemStamps, zone: body.zone, billingMode: body.billingMode,
        coupon: body.coupon ? { code: body.coupon.code, note: body.coupon.note, overrideId: body.coupon.overrideId } : undefined,
      });
      let res;
      try {
        res = await send(body.payments);
      } catch (e: any) {
        // the screen's total can differ from the server's by a rounding fil (per-line tax): retry once with the server's figure
        const total = e?.details?.total;
        if (e?.code === "PAYMENT_MISMATCH" && body.payments.length === 1 && typeof total === "number") res = await send([{ ...body.payments[0], amount: total }]);
        else throw e;
      }
      return done({ session: mapSession(res.session), transaction: res.transaction ? { id: res.transaction.id, receiptNo: res.transaction.receiptNo, total: res.transaction.total } : null });
    }
    if (method === "POST" && (m = /^\/sessions\/([^/]+)\/extend$/.exec(p))) {
      const res = await raw("POST", p, body);
      return done({ session: mapSession(res.session), transaction: { id: res.transaction.id, receiptNo: res.transaction.receiptNo, total: res.transaction.total }, addedMinutes: res.addedMinutes });
    }
    if (method === "POST" && /^\/sessions\/[^/]+\/checkout$/.test(p)) {
      const res = await raw("POST", p, body);
      return done({ ...res, session: mapSession(res.session) });
    }

    // ───────── shifts ─────────
    if (method === "GET" && p === "/shifts/current") {
      const r = await raw("GET", p);
      return done({ shift: r.shift ? camel(r.shift) : null, report: r.report ? camel(r.report) : null });
    }
    if (method === "POST" && p === "/shifts/open") {
      const r = await raw("POST", p, body);
      const cur = await raw("GET", "/shifts/current");
      return done({ shift: camel(cur.shift ?? r.shift) });
    }
    if (method === "POST" && /^\/shifts\/[^/]+\/close$/.test(p)) return done(camel(await raw("POST", p, body)));

    // ───────── customers ─────────
    if (method === "GET" && p === "/customers/search") {
      const r = await raw("GET", path);
      return done({ guardians: r.results.map((g: any) => camel({ ...g, kids: g.kids.map((k: any) => ({ ...k, guardian_id: g.id })) })) });
    }
    if (method === "GET" && p === "/customers/organizations") return done(camel(await raw("GET", p)));

    // ───────── WhatsApp ─────────
    if (method === "GET" && (m = /^\/whatsapp\/conversations\/([^/]+)\/messages$/.exec(p))) {
      const limit = qs(path).get("limit") ?? "100";
      const r = await raw("GET", `/whatsapp/messages?guardianId=${m[1]}&limit=${Math.min(200, Number(limit))}`);
      return done({ messages: camel(r.messages).reverse() });
    }
    if (method === "POST" && p === "/whatsapp/messages") {
      if (body.templateCode) return done(await raw("POST", "/whatsapp/preset", { sessionId: body.sessionId, templateCode: body.templateCode }));
      return done(await raw("POST", "/whatsapp/send", { guardianId: body.guardianId, body: body.text, sessionId: body.sessionId }));
    }
    if (method === "GET" && p === "/whatsapp/templates") return done(camel(await raw("GET", p)));
    if (method === "PUT" && (m = /^\/whatsapp\/templates\/([^/]+)\/(ar|en)$/.exec(p))) {
      const all = await raw("GET", "/whatsapp/templates");
      const t = all.templates.find((x: any) => x.code === m![1] && x.locale === m![2]);
      if (!t) throw Object.assign(new Error("Template not found"), { status: 404, code: "NOT_FOUND" });
      return done(await raw("PUT", `/whatsapp/templates/${t.id}`, { body: body.body }));
    }
    if (method === "PATCH" && (m = /^\/whatsapp\/rules\/([^/]+)$/.exec(p))) {
      const all = await raw("GET", "/whatsapp/templates");
      const mine = all.templates.filter((x: any) => x.code === m![1]);
      for (const t of mine) await raw("PUT", `/whatsapp/templates/${t.id}`, { enabled: body.enabled });
      return done({ templates: mine.map((t: any) => ({ ...t, enabled: body.enabled })) });
    }

    // ───────── bookings ─────────
    if (method === "GET" && p === "/bookings") {
      const r = await raw("GET", path);
      return done({ bookings: r.bookings.map((b: any) => ({ ...camel(b), depositPaid: b.amount_paid })) });
    }
    if (method === "GET" && p === "/bookings/rooms") return done(camel(await raw("GET", p)));
    if (method === "POST" && p === "/bookings") {
      const r = await raw("POST", p, body);
      return done({ ...camel(r), booking: { ...camel(r.booking), depositPaid: r.booking.amount_paid } });
    }

    // ───────── receivables, inventory ─────────
    if (method === "GET" && p === "/receivables") return done(camel(await raw("GET", path)));
    if (method === "GET" && p === "/inventory") {
      const r = await raw("GET", p);
      return done({ items: r.items.map((i: any) => ({ ...camel(i), productId: i.stock_item_id })) });
    }

    // ───────── discounts ─────────
    if (method === "GET" && p === "/discounts/rules") {
      const r = await raw("GET", `/discounts${qs(path).get("all") ? "?all=1" : ""}`);
      return done({ rules: r.rules.map(mapRule) });
    }
    if (method === "PUT" && (m = /^\/discounts\/rules\/([^/]+)$/.exec(p))) {
      const keep = ["name", "nameAr", "kind", "value", "requiresApproval", "requiresNote", "active", "validFrom", "validTo", "params"];
      const r = await raw("PATCH", `/discounts/${m[1]}`, Object.fromEntries(Object.entries(body ?? {}).filter(([k]) => keep.includes(k))));
      return done({ rule: mapRule(r.rule) });
    }
    if (method === "POST" && p === "/discounts/rules") {
      const r = await raw("POST", "/discounts", { code: body.code, name: body.name, nameAr: body.nameAr, kind: body.kind, value: body.value, requiresApproval: body.requiresApproval, requiresNote: body.requiresNote });
      return done({ rule: mapRule(r.rule) });
    }
    if (method === "POST" && p === "/discounts/evaluate") {
      const t = splitTicket(body.passProductId);
      const r = await raw("POST", p, { guardianId: body.guardianId ?? null, kidId: body.kidId ?? null, productId: t.productId, minutes: t.minutes, sessionId: body.sessionId, addons: body.addons ?? [] });
      return done({ base: r.base, options: r.options.map((o: any) => ({ rule: mapRule(o.rule), eligible: o.eligible, reason: o.reason, amount: o.amount })) });
    }
    if (method === "GET" && p === "/discounts/redemptions") {
      const r = await raw("GET", p);
      return done({ redemptions: camel(r.redemptions), today: { total: r.today.total, count: r.today.n } });
    }

    // ───────── receipts ─────────
    if (method === "GET" && (m = /^\/transactions\/([^/]+)$/.exec(p))) {
      const r = await raw("GET", p);
      return done({ transaction: camel(r.transaction), items: camel(r.items), payments: camel(r.payments), discountRule: r.discountRule ? camel(r.discountRule) : null, einvoice: r.einvoice ?? null });
    }

    return { handled: false };
  }
  return { handle };
}
